/**
 * PercentageSlider component with visual slider and linked input field
 * Supports values from 0-100% with react-hook-form integration
 */

import { useEffect, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { clamp, parsePercentage } from "@/lib/money";
import type { FieldConfig } from "../types";

interface PercentageSliderProps<T> {
  field: FieldConfig<T>;
  value: number; // Value as percentage (0-100)
  onChange: (value: number) => void;
  onBlur?: () => void;
  disabled?: boolean;
  id: string;
  placeholder?: string;
  "aria-describedby"?: string;
  min?: number;
  max?: number;
  step?: number;
  showDecimals?: boolean;
}

export function PercentageSlider<T>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  placeholder = "0",
  "aria-describedby": ariaDescribedBy,
  min = 0,
  max = 100,
  step = 1,
  showDecimals = false,
}: PercentageSliderProps<T>) {
  // Internal state for the input display value
  const [inputValue, setInputValue] = useState<string>("");
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Update input value when prop value changes
  useEffect(() => {
    if (!isInputFocused) {
      const decimals = showDecimals ? 1 : 0;
      // Don't use formatPercentage here since we add % sign separately in the UI
      setInputValue((value || 0).toFixed(decimals));
    }
  }, [value, isInputFocused, showDecimals]);

  const handleSliderChange = (values: number[]) => {
    const newValue = values[0];
    onChange(newValue);

    // Update input display - don't use formatPercentage since we add % sign separately
    const decimals = showDecimals ? 1 : 0;
    setInputValue(newValue.toFixed(decimals));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = e.target.value;
    setInputValue(inputText);
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
    // Show raw number for easier editing
    setInputValue(value.toString());
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);

    // Parse and clamp the value
    const parsed = parsePercentage(inputValue);
    const clamped = clamp(parsed, min, max);

    // Round to step
    const rounded = Math.round(clamped / step) * step;

    // Update the form value
    onChange(rounded);

    // Format the display value - don't use formatPercentage since we add % sign separately
    const decimals = showDecimals ? 1 : 0;
    setInputValue(rounded.toFixed(decimals));

    // Call the original onBlur if provided
    onBlur?.();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow Enter to submit the value
    if (e.key === "Enter") {
      handleInputBlur();
    }

    // Arrow keys to increment/decrement
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const newValue = clamp(value + step, min, max);
      onChange(newValue);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const newValue = clamp(value - step, min, max);
      onChange(newValue);
    }
  };

  return (
    <div className="space-y-3">
      {/* Slider with value display */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Slider
            id={`${id}-slider`}
            min={min}
            max={max}
            step={step}
            value={[value || 0]}
            onValueChange={handleSliderChange}
            disabled={disabled}
            aria-describedby={ariaDescribedBy}
            className="w-full"
          />
        </div>

        {/* Compact input field */}
        <div className="relative w-20">
          <Input
            id={id}
            type="text"
            inputMode="decimal"
            placeholder={placeholder}
            disabled={disabled}
            aria-describedby={ariaDescribedBy}
            aria-required={field.required || undefined}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            className="text-right pr-7"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
            %
          </div>
        </div>
      </div>

      {/* Optional min/max labels */}
      {(min !== 0 || max !== 100) && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{min}%</span>
          <span>{max}%</span>
        </div>
      )}
    </div>
  );
}
